import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Button, Row, Col, Alert, Spinner, Modal, Image } from 'react-bootstrap';
import ReviewList from './ReviewList';
import ReviewCalendar from './ReviewCalendar';


export default function UserProfile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersData, setFollowersData] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [followingData, setFollowingData] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(true);

  // profile photo quick menu
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setError('Failed to load user profile');
      }
    } catch (err) {
      setError('Network error loading profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowersData = async () => {
    if (!user?.followers || user.followers.length === 0) {
      setFollowersData([]);
      return;
    }
    setFollowersLoading(true);
    try {
      const response = await fetch(`/api/users/list?ids=${user.followers.join(',')}`);
      if (response.ok) {
        const data = await response.json();
        setFollowersData(data);
      } else {
        setFollowersData([]);
      }
    } catch (err) {
      setFollowersData([]);
    } finally {
      setFollowersLoading(false);
    }
  };

  const handleFollowersClick = () => {
    setShowFollowersModal(true);
    fetchFollowersData();
  };

  const fetchFollowingData = async () => {
    if (!user?.following || user.following.length === 0) {
      setFollowingData([]);
      return;
    }
    setFollowingLoading(true);
    try {
      const response = await fetch(`/api/users/list?ids=${user.following.join(',')}`);
      if (response.ok) {
        const data = await response.json();
        setFollowingData(data);
      } else {
        setFollowingData([]);
      }
    } catch (err) {
      setFollowingData([]);
    } finally {
      setFollowingLoading(false);
    }
  };

  const handleFollowingClick = () => {
    setShowFollowingModal(true);
    fetchFollowingData();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleProfileImageUpdate = (newImageUrl) => {
    setUser(prevUser => ({
      ...prevUser,
      profileImage: newImageUrl
    }));
  };

  const getProfileImageUrl = () => {
    if (user?.profileImage) {
      // If it's already a full URL, return as is
      if (user.profileImage.startsWith('http')) {
        return user.profileImage;
      }
      // Otherwise, prepend the server URL
      return `http://localhost:5000${user.profileImage}`;
    }
    return null;
  };

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('profileImage', file);
      const res = await fetch('/api/users/profile-image', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Upload failed');
      }
      const data = await res.json();
      handleProfileImageUpdate(data.profileImage);
      setShowPhotoMenu(false);
    } catch (err) {
      alert(err.message || 'Failed to upload image');
    } finally {
      setPhotoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeProfilePhoto = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setPhotoBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/profile-image', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Delete failed');
      }
      handleProfileImageUpdate(null);
      setShowPhotoMenu(false);
    } catch (err) {
      alert(err.message || 'Failed to remove image');
    } finally {
      setPhotoBusy(false);
    }
  };


  if (loading) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading profile...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <Row>
        <Col>
          {/* User Info Card */}
          <Card className="mb-4">
            <Card.Body>
              {/* Hidden file input for profile photo */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onFileChosen}
              />
              <div className="text-center">
                {getProfileImageUrl() ? (
                  <Image
                    src={getProfileImageUrl()}
                    alt="Profile"
                    roundedCircle
                    onClick={() => setShowPhotoMenu(true)}
                    style={{ 
                      width: '100px', 
                      height: '100px', 
                      objectFit: 'cover',
                      margin: '0 auto 20px',
                      cursor: 'pointer'
                    }}
                    className="border"
                    title="Click to change or remove photo"
                  />
                ) : (
                  <div
                    onClick={() => setShowPhotoMenu(true)}
                    title="Click to add a photo"
                    style={{ 
                      width: '100px', 
                      height: '100px', 
                      borderRadius: '50%', 
                      backgroundColor: '#007bff', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      fontSize: '2rem',
                      color: 'white',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                )}
                <h2>{user?.username}</h2>
                <div className="d-flex justify-content-center mb-2" style={{ gap: 24 }}>
                  <div
                    className="text-center"
                    style={{ cursor: 'pointer', borderRadius: 8, padding: '4px 12px', border: '1px solid #eee', minWidth: 60 }}
                    onClick={handleFollowersClick}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{user.followersCount ?? user.followers?.length ?? 0}</div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Followers</div>
                  </div>
                  <div
                    className="text-center"
                    style={{ cursor: 'pointer', borderRadius: 8, padding: '4px 12px', border: '1px solid #eee', minWidth: 60 }}
                    onClick={handleFollowingClick}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{user.followingCount ?? user.following?.length ?? 0}</div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Following</div>
                  </div>
                </div>
                <Modal show={showFollowersModal} onHide={() => setShowFollowersModal(false)} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>Followers</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {followersLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" /> Loading...
                      </div>
                    ) : followersData.length > 0 ? (
                      <div>
                        <ul className="list-unstyled">
                          {followersData.map((follower) => (
                            <li key={follower._id} className="mb-2">
                              <a
                                href={user._id === follower._id ? "/profile" : `/user/${follower._id}`}
                                style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                                onClick={() => setShowFollowersModal(false)}
                              >
                                {follower.username}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p>No followers found.</p>
                    )}
                  </Modal.Body>
                </Modal>
                <Modal show={showFollowingModal} onHide={() => setShowFollowingModal(false)} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>Following</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {followingLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" size="sm" /> Loading...
                      </div>
                    ) : followingData.length > 0 ? (
                      <div>
                        <ul className="list-unstyled">
                          {followingData.map((following) => (
                            <li key={following._id} className="mb-2">
                              <a
                                href={user._id === following._id ? "/profile" : `/user/${following._id}`}
                                style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                                onClick={() => setShowFollowingModal(false)}
                              >
                                {following.username}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p>Not following anyone.</p>
                    )}
                  </Modal.Body>
                </Modal>
                <p className="text-muted">
                  Member since {user?.createdAt ? formatDate(user.createdAt) : 'Unknown'}
                </p>
              </div>
            </Card.Body>
          </Card>

          <Modal show={showPhotoMenu} onHide={() => setShowPhotoMenu(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Profile Photo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={triggerFilePicker}
                  disabled={photoBusy}
                >
                  {photoBusy ? 'Uploading…' : 'Upload New Photo'}
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={removeProfilePhoto}
                  disabled={photoBusy || !getProfileImageUrl()}
                >
                  {photoBusy ? 'Removing…' : 'Remove Current Photo'}
                </Button>
              </div>
            </Modal.Body>
          </Modal>

          {/* Switch button BELOW upload section */}
          <div className="d-flex justify-content-end mt-3 mb-2">
            <Button
              variant="outline-dark"
              size="sm"
              onClick={() => setShowCalendar(s => !s)}
            >
              {showCalendar ? '› Reviews' : '‹ Calendar'}
            </Button>
          </div>
                    
          {/* Sliding panels: left = Calendar, right = Reviews */}
          <div className="profile-panels-wrap">
            <div
              className="profile-panels-track"
              style={{ transform: showCalendar ? 'translateX(0%)' : 'translateX(-50%)' }}
            >
              {/* Left panel: Calendar */}
              <div className="profile-panel">
                <ReviewCalendar />
              </div>

              {/* Right panel: your existing two columns of reviews */}
              <div className="profile-panel">
                <Row className="mt-1">
                  <Col md={6}>
                    <div className="reviews-pane">
                      <ReviewList
                        mode="user"
                        showItemInfo={true}
                        currentUser={user}
                        typeFilter="track"
                        title="My Track Reviews"
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="reviews-pane">
                      <ReviewList
                        mode="user"
                        showItemInfo={true}
                        currentUser={user}
                        typeFilter="album"
                        title="My Album Reviews"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
} 